import { CloseIcon, Drawer } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { darkModeRoot } from 'src/app/App.css';
import { IconButton } from 'src/components/Buttons/IconButton';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Button, Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { PlayIcon } from 'src/svgs/PlayIcon';
import { ContentId } from 'src/utils/constants/contentId';

import { StrategyCalculatorContent } from './StrategyCalculatorPanel';
import {
  BID_STRATEGIES_VIDEO_POSTER_URL,
  BID_STRATEGIES_VIDEO_URL,
  DRAG_CLOSE_THRESHOLD,
} from './StrategyCalculatorPanel.constants';
import * as styles from './StrategyCalculatorPanel.css';

/* ── Mobile video modal (full-viewport overlay) ── */

const MobileVideoModal = ({ onClose }: { onClose: () => void }) => {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className={styles.mobileVideoOverlay}>
      <IconButton
        icon={<CloseIcon size={vars.iconSize.m} />}
        titleContentId={ContentId.Close}
        onClick={onClose}
        className={styles.mobileVideoClose}
      />
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Tutorial video hosted externally without caption tracks */}
      <video
        className={styles.mobileVideoPlayer}
        src={BID_STRATEGIES_VIDEO_URL}
        poster={BID_STRATEGIES_VIDEO_POSTER_URL}
        controls
        autoPlay
        playsInline
      />
    </div>,
    document.body
  );
};

/* ── Desktop video modal overlay ── */

/** Fetch the video as a blob and return an object URL for smoother playback */
const fetchVideoBlob = async (): Promise<string> => {
  const response = await fetch(BID_STRATEGIES_VIDEO_URL);
  if (!response.ok) {
    throw new Error(`Failed to download video (${response.status})`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

const DesktopVideoModal = ({ onClose }: { onClose: () => void }) => {
  const videoTitle = useContent(ContentId.StrategyVideoTitle);
  const loadingText = useContent(ContentId.Loading);

  const {
    data: videoBlobUrl,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['strategyVideo', BID_STRATEGIES_VIDEO_URL],
    queryFn: fetchVideoBlob,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000, // keep cached blob URL for 5 minutes after unmount
    retry: false,
  });

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Backdrop click-to-dismiss is a standard modal UX pattern
    <div
      role="dialog"
      aria-modal="true"
      aria-label={videoTitle}
      className={styles.videoModalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.videoModalContent}>
        <IconButton
          icon={<CloseIcon size={vars.iconSize.m} />}
          titleContentId={ContentId.Close}
          onClick={onClose}
          className={styles.videoModalClose}
        />

        {isLoading && (
          <div className={styles.videoModalLoading}>
            <div className={styles.videoModalSpinner} />
            <p className={styles.videoModalLoadingText}>{loadingText}</p>
          </div>
        )}

        {isError && !isLoading && (
          <div className={styles.videoModalError}>
            <p>
              {error instanceof Error ? error.message : 'Failed to load video'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <Content id={ContentId.Retry} />
            </Button>
          </div>
        )}

        {videoBlobUrl && !isLoading && !isError && (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- Tutorial video hosted externally without caption tracks
          <video
            className={styles.videoModalVideo}
            controls
            autoPlay
            playsInline
            poster={BID_STRATEGIES_VIDEO_POSTER_URL}
            src={videoBlobUrl}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

/* ── Video modal — delegates to mobile or desktop ── */

export const StrategyVideoModal = ({ onClose }: { onClose: () => void }) => {
  const isMobile = useMatchMedia('mobile');

  if (isMobile) {
    return <MobileVideoModal onClose={onClose} />;
  }

  return <DesktopVideoModal onClose={onClose} />;
};

/* ── Video banner card ── */

export const StrategyVideoBanner = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const handleOpen = useCallback(() => setIsVideoOpen(true), []);
  const handleClose = useCallback(() => setIsVideoOpen(false), []);

  return (
    <>
      <div
        className={styles.videoBanner}
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleOpen();
          }
        }}
      >
        <div className={styles.videoBannerPlayButton}>
          <PlayIcon width={60} height={44} />
        </div>
        <div className={styles.videoBannerText}>
          <span className={styles.videoBannerTitle}>
            <Content id={ContentId.StrategyVideoTitle} />
          </span>
          <span className={styles.videoBannerSubtitle}>
            <Content id={ContentId.StrategyVideoSubtitle} />
          </span>
        </div>
      </div>
      {isVideoOpen && <StrategyVideoModal onClose={handleClose} />}
    </>
  );
};

export const StrategyCalculatorDrawer = ({
  isOpen,
  onClose,
  eventDateStr,
  onRecommendationChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  eventDateStr?: string | null;
  onRecommendationChange?: (strategyId: string) => void;
}) => {
  const { isDarkMode } = useSiteTheme();
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragDelta = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    dragDelta.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    dragDelta.current = delta;

    if (contentRef.current && delta > 0) {
      contentRef.current.style.transform = `translateY(${delta}px)`;
      contentRef.current.style.transition = 'none';
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (dragDelta.current > DRAG_CLOSE_THRESHOLD) {
      if (contentRef.current) {
        contentRef.current.style.transition = 'transform 0.3s ease-in';
        contentRef.current.style.transform = 'translateY(100%)';
        contentRef.current.addEventListener('transitionend', () => onClose(), {
          once: true,
        });
      } else {
        onClose();
      }
    } else if (contentRef.current) {
      contentRef.current.style.transition = 'transform 0.2s ease-out';
      contentRef.current.style.transform = 'translateY(0)';
    }

    dragStartY.current = null;
    dragDelta.current = 0;
  }, [onClose]);

  return (
    <Drawer.Root
      opened={isOpen}
      onClose={onClose}
      size="90%"
      position="bottom"
      zIndex={1060}
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
      closeOnClickOutside
    >
      <Drawer.Overlay />
      <Drawer.Content
        ref={contentRef}
        bg={vars.color.backgroundPrimary}
        className={styles.drawerBorderRadius}
        radius="md"
      >
        <Drawer.Header
          bg={vars.color.backgroundPrimary}
          className={styles.drawerHeader}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            width="full"
            direction="column"
          >
            <div className={styles.drawerHandleBar} />
          </Stack>
        </Drawer.Header>
        <Drawer.Body
          bg={vars.color.backgroundPrimary}
          className={styles.drawerBody}
        >
          <StrategyCalculatorContent
            onClose={onClose}
            showCloseButton={false}
            panelClassName={styles.drawerPanel}
            isMobile
            eventDateStr={eventDateStr}
            onRecommendationChange={onRecommendationChange}
          />
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};
